import { Box, ClickAwayListener, Collapse, Dialog, IconButton, Paper } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// RoundIconButton replaced with standard IconButton

import { SearchInput } from './SearchInput';

const MemoizedSearchInput = React.memo(SearchInput);

export const MAX_WIDTH = 300;
export const TYPING_TIMEOUT = 500;

interface SearchBoxProps {
    color?: string;
    initiallyOpen?: boolean;
    isMobile?: boolean;
    containSuggestions?: boolean;
}

export default function SearchBox({
    color,
    initiallyOpen = false,
    isMobile = false,
    containSuggestions = false,
}: SearchBoxProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const layoutStrings = useMemo(() => getLanguageTexts(lang).layout, [lang]);
    const iconColor = theme.palette.primary.main;

    const [searchValue, setSearchValue] = useState<string>('');
    const [open, setOpen] = useState<boolean>(initiallyOpen);
    const inputRef = useRef<HTMLInputElement>(null);
    const justOpenedRef = useRef(false); // <- Wichtig für ClickAwayFix

    const toggleOpen = useCallback(() => {
        setOpen((prev) => {
            const newOpen = !prev;
            if (newOpen) {
                justOpenedRef.current = true;
                setTimeout(() => {
                    justOpenedRef.current = false;
                }, 100);
            }
            return newOpen;
        });
    }, []);

    const handleClose = () => {
        setOpen(false);
        setSearchValue(''); // optional: Inhalt löschen
    };

    // Fokus auf das Input-Feld, wenn geöffnet
    useEffect(() => {
        if (open && inputRef.current) {
            const delay = initiallyOpen ? 50 : 150;
            setTimeout(() => inputRef.current?.focus(), delay);
        }
    }, [open, initiallyOpen]);

    // ESC zum Schließen
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleClose();
        }
        if (e.key === 'Enter') {
            console.log('Enter pressed: searching for', searchValue);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                height: containSuggestions ? 'auto' : 32,
                width: containSuggestions ? '100%' : 'auto',
            }}
        >
            <IconButton
                title={layoutStrings.search}
                onClick={toggleOpen}
                sx={{
                    color: theme.palette.primary.main,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'scale(1.05)',
                    },
                }}
            >
                <Search size={20} />
            </IconButton>

            <Collapse in={open} orientation="horizontal" timeout={200}>
                <ClickAwayListener
                    onClickAway={() => {
                        if (!justOpenedRef.current) {
                            handleClose();
                        }
                    }}
                >
                    <Paper
                        elevation={4}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: isMobile ? 0 : { xs: theme.spacing(1), sm: theme.spacing(4), md: theme.spacing(6) },
                            left: isMobile ? 0 : 'auto',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            width: isMobile ? '100%' : MAX_WIDTH,
                            px: 1,
                            py: 0.5,
                            borderRadius: isMobile ? 0 : 2,
                            border: `1px solid ${theme.palette.primary.main}`,
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: theme.shadows[3],
                        }}
                    >
                        <MemoizedSearchInput
                            open={open}
                            setOpen={setOpen}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            customColor={iconColor}
                            inputRef={inputRef}
                            onKeyDown={handleKeyDown}
                            isMobile={isMobile}
                            containSuggestions={containSuggestions}
                        />

                        {!isMobile && (
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    ml: 0.5,
                                    color: theme.palette.primary.main,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        transform: 'scale(1.05)',
                                    },
                                }}
                                aria-label={'Schließen'}
                            >
                                <X size={16} />
                            </IconButton>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Collapse>
        </Box>
    );
}

SearchBox.displayName = 'SearchBox';

/**
 * A mobile-optimized version of the SearchBox component.
 * @returns {JSX.Element} - The rendered mobile search box component.
 */
export function MobileSearchBox() {
    const [showFullSearch, setShowFullSearch] = useState(false);
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const layoutStrings = useMemo(() => getLanguageTexts(lang).layout, [lang]);

    return (
        <>
            <IconButton
                onClick={() => setShowFullSearch(true)}
                sx={{
                    color: theme.palette.primary.main,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        transform: 'scale(1.05)',
                    },
                }}
            >
                <Search size={20} />
            </IconButton>

            <Dialog
                open={showFullSearch}
                onClose={() => setShowFullSearch(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        m: 0,
                        width: '100%',
                        maxHeight: '90vh',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    },
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                            pb: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Box sx={{ typography: 'h6', color: theme.palette.primary.main }}>{layoutStrings.search}</Box>
                        <IconButton
                            onClick={() => setShowFullSearch(false)}
                            sx={{
                                color: theme.palette.primary.main,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <X size={20} />
                        </IconButton>
                    </Box>

                    <Box
                        id="mobileSearchContainer"
                        sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            mx: 1,
                            minHeight: 300,
                        }}
                    >
                        <SearchBox initiallyOpen={true} isMobile={true} containSuggestions={true} />
                    </Box>
                </Box>
            </Dialog>
        </>
    );
}
